<template>
  <v-container fluid class="pa-0">
    <!-- 🔹 Chat Header -->
    <v-app-bar color="primary" dark flat app>
      <v-btn icon @click="$router.push({ name: 'chat-list' })">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>

      <v-toolbar-title class="font-weight-bold">
        {{ partner?.name || "Chat" }}
      </v-toolbar-title>

      <v-spacer></v-spacer>
      <UserOnlineStatus v-if="partner?.id" :userId="partner.id" class="mr-2" />
    </v-app-bar>

    <!-- 🔹 Profile Info Box -->
    <v-card
      v-if="conversation?.Profile"
      flat
      class="profile-info-card px-4 py-3 d-flex align-center justify-space-between"
    >
      <div class="d-flex align-center">
        <v-avatar size="48" class="mr-3">
          <v-img
            :src="
              conversation.Profile.gender === 'Female'
                ? require('@/assets/avatars/wife.png')
                : require('@/assets/avatars/brother.png')
            "
          />
        </v-avatar>

        <div>
          <div class="font-weight-bold text-subtitle-1">
            {{ conversation.Profile.name }}
          </div>
          <div class="text-caption grey--text text--darken-1">
            {{ calculateAge(conversation.Profile.dob) }} yrs —
            {{ conversation.Profile.city }}, {{ conversation.Profile.state }}
          </div>
        </div>
      </div>

      <v-btn
        small
        color="primary"
        rounded
        outlined
        @click="viewProfile(conversation.Profile.id)"
      >
        View Profile
      </v-btn>
    </v-card>

    <!-- 🔹 Chat Messages -->
    <v-container class="chat-container pa-4">
      <div
        v-for="(msg, i) in messages"
        :key="i"
        :class="[
          'd-flex mb-2',
          msg.FromUserId === currentUser.id ? 'justify-end' : 'justify-start',
        ]"
      >
        <div
          :class="[
            'pa-3 rounded-xl',
            msg.FromUserId === currentUser.id
              ? 'sent-message'
              : 'received-message',
          ]"
        >
          <div class="text-body-2">{{ msg.content }}</div>
          <div class="text-caption grey--text text-right mt-1">
            {{ formatTime(msg.createdAt) }}
          </div>
        </div>
      </div>
    </v-container>

    <v-divider></v-divider>

    <!-- 🔹 Message Input -->
    <v-footer app height="auto" class="pa-2 position-relative" color="white">
      <!-- 🟢 Typing Indicator -->
      <transition name="fade">
        <div
          v-if="isTyping"
          class="typing-bubble d-flex align-center px-3 py-2 elevation-2"
        >
          <v-avatar size="24" class="mr-2">
            <v-img
              :src="
                partner.gender === 'Female'
                  ? require('@/assets/avatars/wife.png')
                  : require('@/assets/avatars/brother.png')
              "
            />
          </v-avatar>
          <span class="typing-text">
            {{ partner.name }} is typing <span class="dot"></span
            ><span class="dot"></span><span class="dot"></span>
          </span>
        </div>
      </transition>

      <v-row no-gutters align="center">
        <v-col>
          <v-text-field
            v-model="newMessage"
            placeholder="Type a message..."
            outlined
            dense
            hide-details
            @keyup.enter="sendMessage"
            @input="notifyTyping"
            class="flex-grow-1 mr-2"
          />
        </v-col>
        <v-col cols="auto">
          <v-btn color="primary" icon @click="sendMessage">
            <v-icon>mdi-send</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-footer>
  </v-container>
</template>

<script>
import chatService from "@/modules/Chat/service";
import socket from "@/socket";
import { mapGetters } from "vuex/dist/vuex.common.js";
import UserOnlineStatus from "@/components/UserOnlineStatus.vue";

export default {
  props: ["conversationId"],
  data() {
    return {
      localConversationId: null,
      messages: [],
      newMessage: "",
      conversation: null,
      partner: null,
      isTyping: false,
      typingTimeout: null,
    };
  },
  computed: {
    ...mapGetters("global", ["currentUser"]),
  },
  beforeDestroy() {
    socket.off("receiveMessage");
    socket.off("typing");
  },
  components: { UserOnlineStatus },
  async created() {
    try {
      const cid = Number(
        this.conversationId || this.$route.params.conversationId
      );
      if (!cid) throw new Error("Missing conversationId");
      this.localConversationId = cid;

      // Load conversation details
      const result = await chatService.getById(cid);
      this.conversation = result.data || result;
      console.log("Loaded conversation:", this.conversation);

      const userId = this.currentUser.id;
      this.partner =
        this.conversation.UserAId === userId
          ? this.conversation.UserB
          : this.conversation.UserA;

      // Load existing messages
      const messagesRes = await chatService.getMessages(cid);
      this.messages = messagesRes.data?.rows || messagesRes.data;
      this.scrollToBottom();

      // ✅ Join both user and conversation rooms
      socket.emit("join", {
        userId: this.currentUser.id,
        conversationId: cid,
      });

      // ✅ Listen for new messages in this conversation
      socket.on("receiveMessage", (msg) => {
        const incomingCid =
          msg.conversationId || msg.ConversationId || msg.conversation_id;
        if (Number(incomingCid) === Number(this.localConversationId)) {
          this.messages.push(msg);
          this.scrollToBottom();
        }
      });

      // ✅ Typing indicator listener
      socket.on("typing", (data) => {
        if (this.partner && data.fromUserId === this.partner.id) {
          this.isTyping = data.isTyping;
          clearTimeout(this.typingTimeout);
          if (data.isTyping) {
            this.typingTimeout = setTimeout(
              () => (this.isTyping = false),
              1000
            );
          }
        }
      });
    } catch (error) {
      console.error("Error loading conversation:", error);
    }
  },
  methods: {
    calculateAge(dob) {
      if (!dob) return "";
      const birth = new Date(dob);
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const m = today.getMonth() - birth.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
      return age;
    },
    viewProfile(profileId) {
      if (!profileId) return;
      this.$router.push({ name: "public-profile-view", params: { profileId } });
    },
    notifyTyping() {
      // Emit typing status when user starts typing
      socket.emit("typing", {
        fromUserId: this.currentUser.id,
        toUserId: this.partner?.id,
        isTyping: true,
      });

      // Optional debounce — if user stops typing, turn off indicator after delay
      clearTimeout(this.typingTimeout);
      this.typingTimeout = setTimeout(() => {
        socket.emit("typing", {
          fromUserId: this.currentUser.id,
          toUserId: this.partner?.id,
          isTyping: false,
        });
      }, 1500);
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$el.querySelector(".chat-container");
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    formatTime(datetime) {
      if (!datetime) return "";
      const d = new Date(datetime);
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    },
    async sendMessage() {
      if (!this.newMessage.trim()) return;
      if (!this.localConversationId || !this.partner?.id) return;

      const payload = {
        conversationId: this.localConversationId,
        toUserId: this.partner.id,
        content: this.newMessage.trim(),
        user: { id: this.currentUser.id }, // 👈 backend expects this shape
      };

      // Show message instantly (optimistic UI)
      const tempMsg = {
        content: payload.content,
        FromUserId: this.currentUser.id,
        createdAt: new Date(),
      };
      this.messages.push(tempMsg);
      this.scrollToBottom();

      try {
        // Save to backend
        const res = await chatService.sendMessage(
          this.localConversationId,
          payload
        );
        const saved = res.data?.data || res.data;

        // Notify recipient in real-time
        socket.emit("sendMessage", saved);

        this.newMessage = "";
      } catch (e) {
        console.error("Send failed:", e);
      }
    },
  },
};
</script>

<style scoped>
.chat-container {
  overflow-y: auto;
  flex-grow: 1;
  height: calc(100vh - 150px);
  display: flex;
  flex-direction: column;
}
.sent-message {
  background-color: #e3f2fd;
  color: #0d47a1;
  max-width: 75%;
  word-wrap: break-word;
  border-top-right-radius: 0 !important;
}
.received-message {
  background-color: #f5f5f5;
  color: #212121;
  max-width: 75%;
  word-wrap: break-word;
  border-top-left-radius: 0 !important;
}

.typing-bubble {
  position: absolute;
  bottom: 64px; /* 👈 sits right above bottom nav bar */
  left: 12px;
  background: #fff;
  border-radius: 20px;
  border: 1px solid #e0e0e0;
  color: #424242;
  font-size: 13px;
  z-index: 20;
  animation: fadeUp 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

/* bubble entry */
@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Dots animation */
.typing-text .dot {
  height: 6px;
  width: 6px;
  margin-left: 3px;
  background-color: #757575;
  border-radius: 50%;
  display: inline-block;
  animation: typingBlink 1.4s infinite both;
}
.typing-text .dot:nth-child(1) {
  animation-delay: 0s;
}
.typing-text .dot:nth-child(2) {
  animation-delay: 0.2s;
}
.typing-text .dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typingBlink {
  0%,
  80%,
  100% {
    opacity: 0.2;
  }
  40% {
    opacity: 1;
  }
}
.profile-context-card {
  background-color: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
  position: sticky;
  top: 64px; /* right below the app bar */
  z-index: 5;
}
</style>